import { Box, Button, Image, Typography } from "@outschool/backpack";
import * as FilestackUrls from "@outschool/filestack-urls";
import { Trans, useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import { dayjs } from "@outschool/time";
import { gql } from "@outschool/ui-apollo";
import { Space } from "@outschool/ui-legacy-component-library";
import { Screen, useNavigation } from "@outschool/ui-utils";
import React from "react";

import calendarImage from "../../../images/calendar.png";
import {
  FullCurrentUserFragmentFragment,
  MultipleMeetingsModal_ActivityFragment
} from "../../../shared/generated/graphql";
import { useTimeZone } from "../../providers/TimeZoneProvider";

type MultipleMeetingsModalProps = {
  activity: MultipleMeetingsModal_ActivityFragment;
  payingUser: FullCurrentUserFragmentFragment;
  startTime: Date;
  endTime: Date;
  onClose: () => void;
  durationWeeks: number | null;
  actualWeeklyMeetings: number;
};

function MultipleMeetingsModal({
  activity,
  payingUser,
  startTime,
  endTime,
  onClose,
  durationWeeks,
  actualWeeklyMeetings
}: MultipleMeetingsModalProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\MultipleMeetingsModal"
  );

  const { timeZone } = useTimeZone();

  const startDate = dayjs.tz(startTime, timeZone);
  const startDateFormatted = Time.formatDate(startTime, timeZone, true);
  let endDate;

  /* durationWeeks can be null, which indicates a recurring meeting that ends when the parent cancels.
  This means there is no end date. Otherwise, the endDate is (durationWeeks - 1) weeks from startDate 
  since we need to count the first meeting too. */
  if (durationWeeks) {
    endDate = startDate.add(durationWeeks - 1, "week");
  }

  const navigate = useNavigation();
  const resolutionMultiplier = Screen.useResolutionMultiplier();

  const suggestedWeeklyMeetings = activity.suggestedWeeklyMeetings ?? 1;

  const handleAddMoreMeetingsClick = () => {
    const newPath = window.location.pathname.replace("/enroll", "");
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set("enrollableTimesModal", "true");
    queryParams.set("unselectTime", "true");
    navigate(`${newPath}?${queryParams.toString()}`);
  };

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        alignItems: "flex-start",
        marginBottom: "1em"
      }}
    >
      <Typography variant="h5">
        {t("Thank you! Your enrollment is confirmed.")}
      </Typography>
      <Space y={24} />
      <Typography>
        {t("Your order receipt will be sent to {{email}}", {
          email: payingUser.email
        })}
      </Typography>
      <Space y={24} />
      <Box flex sx={{ marginBottom: "24px" }}>
        {!!activity.details.photo && (
          <Box
            sx={{
              backgroundImage:
                "url('" +
                FilestackUrls.imageUrl(activity.details.photo, {
                  h: 75 * resolutionMultiplier,
                  w: 75 * resolutionMultiplier
                }) +
                "')",

              width: "5em",
              minWidth: "5em",
              height: "5em",
              borderRadius: "0.25em",
              backgroundSize: "cover",
              marginRight: "1em"
            }}
          />
        )}
        <Box flex sx={{ flexDirection: "column" }}>
          <Typography variant="h6">{activity.title}</Typography>
          <Space y={8} />
          <Typography>
            {t("Meet weekly: {{time}}", {
              time: Time.weeklyDayOfWeekWithDuration(
                startTime,
                endTime,
                timeZone
              )
            })}
          </Typography>
          <Box flex>
            <Typography>
              {t("Start: {{startDateFormatted}}", {
                startDateFormatted
              })}
            </Typography>
            &nbsp;
            {"-"}
            &nbsp;
            <Typography>
              {t("End: {{endDateString}}", {
                endDateString: durationWeeks
                  ? Time.formatDate(endDate, timeZone, true)
                  : "Until I cancel"
              })}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        flex
        sx={{ background: "#E4F0FB", borderRadius: "8px", padding: "8px" }}
      >
        <Image src={calendarImage} width={60} height={60} alt={t`calendar`} />
        <Box
          flex
          sx={{
            flexDirection: "column",
            padding: "8px"
          }}
        >
          <Typography>
            <Trans t={t}>
              This educator suggests meeting{" "}
              <Typography
                variant="inherit"
                style={{ fontWeight: "fontWeightBold" }}
              >
                {{ suggestedWeeklyMeetings }} times per week
              </Typography>
              . You have <b>{{ actualWeeklyMeetings }}</b> meetings scheduled
              out of the recommended <b>{{ suggestedWeeklyMeetings }}</b> for
              the week of {startDateFormatted}.
            </Trans>
          </Typography>
          <Space y={16} />
          <Box flex>
            <Button onClick={handleAddMoreMeetingsClick} variant="contained">
              {t("Add more meetings")}
            </Button>
            <Space x={16} />
            <Button onClick={onClose} variant="link" color="inherit">
              {t("I will do it later")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

MultipleMeetingsModal.fragments = {
  activity: gql`
    fragment MultipleMeetingsModal_activity on Activity {
      uid
      details {
        photo
      }
      title
      titleTranslated
      suggestedWeeklyMeetings
    }
  `
};

export default MultipleMeetingsModal;
